// ─── theme.js ─────────────────────────────────────────────────────────────
// Single source of truth for the Vinarai palette + opacity tokens.
//
// Two themes:
//   - 'red'   (default): the existing dark obsidian / burgundy / gold look
//   - 'white' (alt):     warm ivory background, charcoal text, burgundy
//                        and gold as accents, cellar kept on the header
//                        banner for visual continuity
//
// Components consume the active theme via `useTheme()` (see
// src/context/ThemeProvider.jsx). Pages still using local `const C = {...}`
// declarations should migrate by importing useTheme() and reading from
// `theme.colors` instead -- the constants here mirror the per-page C
// objects so the migration is mostly mechanical.
//
// The opacity tokens replace the scattered rgba(244,239,231,0.X) literals.
// They translate intent ("textPrimary") into a number, so the same dim-text
// fix doesn't have to be repeated per file.

// ── Brand color palette ─────────────────────────────────────────────────
//
// Both themes share the brand colors below; what changes is which colors
// are used as the BACKGROUND vs the TEXT vs the ACCENT.
const BRAND = {
  obsidian: '#0E0C12',  // near-black; red theme background
  cellar:   '#3A0D18',  // deep wine; banner / overlay surfaces both themes
  burgundy: '#7B2040',  // brand color; CTA gradient, pill backgrounds
  gold:     '#C4965A',  // accent color; active states, icons, tags
  ivory:    '#F4EFE7',  // warm off-white; red-theme text, white-theme bg
  charcoal: '#2A1F25',  // soft dark; white-theme text (less harsh than obsidian on ivory)
}

// ── Helpers for opacity mixing ──────────────────────────────────────────
//
// Build an rgba() string from a hex code + alpha. `alphaIvory(0.7)` is the
// equivalent of the legacy `rgba(244,239,231,0.7)` literal but lets the
// theme decide which color we're tinting.
function rgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

// ── Opacity scale for text + borders ────────────────────────────────────
//
// Use these instead of raw 0.X values. textSecondary is the post-styling-
// pass-round-1 floor for body copy on dark backgrounds; bumping below 0.55
// introduces the same legibility issues we just fixed.
//
// We keep TWO scales now — the dark-theme defaults below + a white-theme
// override (OPACITY_WHITE) immediately after. Charcoal-on-ivory needs
// higher alpha than ivory-on-obsidian for the same WCAG AA contrast
// (the math: at 0.55 alpha, charcoal blends to ~#857C7C against ivory,
// which only hits 3.5:1 — fails AA. White theme bumps everything ~10pp
// to clear AA and restore visual hierarchy lost in the round-1 pass).
//
// VIN-003, VIN-007, VIN-010, VIN-012, VIN-020, VIN-026, VIN-034, VIN-037
// all stem from this scale being too faint on light bg. Single change.
export const OPACITY = {
  textPrimary:     0.95,  // bright body text (titles, active labels)
  textSecondary:   0.78,  // sub-headings, card descriptions
  textMuted:       0.65,  // small captions, "X items" counts
  textTertiary:    0.55,  // footer copy, decorative metadata
  textDisabled:    0.4,   // disabled state copy (still legible-ish)
  borderEmphasis:  0.55,  // hover / active borders
  borderDefault:   0.28,  // standard card outlines
  borderSubtle:    0.18,  // dividers between sections
}

// White-theme override — values aligned with the CSS-var equivalents
// in src/index.css [data-theme="white"] block so JS-driven inline
// styles (theme.text.X) and CSS-driven utility classes (.app-text-X /
// var(--app-text-X)) produce identical contrast. Charcoal-on-ivory
// needs higher alpha than ivory-on-obsidian for the same WCAG AA
// contrast (math: at 0.55 alpha, charcoal blends to ~#857C7C against
// ivory for only 3.5:1, fails AA).
export const OPACITY_WHITE = {
  textPrimary:     1.00,  // was 0.95 — solid charcoal at top tier
  textSecondary:   0.85,  // was 0.78
  textMuted:       0.75,  // was 0.65
  textTertiary:    0.65,  // was 0.55 — clears AA on body copy
  textDisabled:    0.50,  // was 0.40 — disabled stays visible
  borderEmphasis:  0.55,  // unchanged — already enough for ivory
  borderDefault:   0.40,  // was 0.28 — card edges visible
  borderSubtle:    0.28,  // was 0.18 — dividers don't ghost out
}

// ── Theme definitions ───────────────────────────────────────────────────
//
// Each theme is the full set of resolved colors + token strings. Components
// read these by tier: theme.colors for solids, theme.text for opacity-
// derived rgba strings, theme.borders for outline styles.
function buildRedTheme() {
  return {
    name: 'red',
    isDark: true,
    colors: {
      // The 5 brand-named colors so existing callers porting from
      // `const C = { obsidian, cellar, burgundy, gold, ivory }` can
      // do a one-line swap to `const C = useTheme().colors`.
      obsidian: BRAND.obsidian,
      cellar:   BRAND.cellar,
      burgundy: BRAND.burgundy,
      gold:     BRAND.gold,
      ivory:    BRAND.ivory,

      // Semantic aliases used by chrome (page background, primary text).
      // White theme overrides these; red theme uses the dark-mode set.
      // bg is cellar (was obsidian) so the body matches the PageHeader +
      // BottomNav gradients which both transition through cellar/burgundy
      // — pure obsidian was producing a hard horizontal step where the
      // chrome ended and the body began.
      bg:           BRAND.cellar,
      surface:      BRAND.cellar,
      headerBanner: BRAND.cellar,
      textPrimary:  BRAND.ivory,
      accent:       BRAND.gold,
      ctaStart:     BRAND.burgundy,
      ctaEnd:       BRAND.cellar,

      // Drinking-window bar fill (gold on red theme, burgundy on white).
      drinkWindowBar: BRAND.gold,
    },
    // Pre-built rgba strings keyed by intent. Use these instead of raw
    // 'rgba(244,239,231,0.X)' literals.
    text: {
      primary:    rgba(BRAND.ivory, OPACITY.textPrimary),
      secondary:  rgba(BRAND.ivory, OPACITY.textSecondary),
      muted:      rgba(BRAND.ivory, OPACITY.textMuted),
      tertiary:   rgba(BRAND.ivory, OPACITY.textTertiary),
      disabled:   rgba(BRAND.ivory, OPACITY.textDisabled),
    },
    borders: {
      emphasis:   rgba(BRAND.gold, OPACITY.borderEmphasis),
      default:    rgba(BRAND.gold, OPACITY.borderDefault),
      subtle:     rgba(BRAND.gold, OPACITY.borderSubtle),
    },
  }
}

function buildWhiteTheme() {
  return {
    name: 'white',
    isDark: false,
    colors: {
      // Brand colors are the same; what changes is how they're used.
      obsidian: BRAND.obsidian,
      cellar:   BRAND.cellar,
      burgundy: BRAND.burgundy,
      gold:     BRAND.gold,
      ivory:    BRAND.ivory,

      // Inverted role assignments for white mode:
      bg:           BRAND.ivory,       // warm ivory page background
      surface:      '#EDE7DC',         // slightly warmer surface (parchment) for cards
      headerBanner: BRAND.cellar,      // cellar kept on the banner for continuity
      textPrimary:  BRAND.charcoal,    // soft dark, less harsh than obsidian on ivory
      accent:       BRAND.gold,        // gold remains the accent
      ctaStart:     BRAND.burgundy,    // CTA gradient unchanged (red on ivory pops)
      ctaEnd:       BRAND.cellar,

      // Drinking-window bar fill: burgundy on white per user direction.
      drinkWindowBar: BRAND.burgundy,
    },
    text: {
      primary:    rgba(BRAND.charcoal, OPACITY_WHITE.textPrimary),
      secondary:  rgba(BRAND.charcoal, OPACITY_WHITE.textSecondary),
      muted:      rgba(BRAND.charcoal, OPACITY_WHITE.textMuted),
      tertiary:   rgba(BRAND.charcoal, OPACITY_WHITE.textTertiary),
      disabled:   rgba(BRAND.charcoal, OPACITY_WHITE.textDisabled),
    },
    borders: {
      // Borders shift from gold (which reads as decorative on dark) to
      // burgundy (which reads as a clean accent line on ivory). White-
      // theme opacities are bumped (see OPACITY_WHITE) so card edges
      // and dividers actually read against ivory.
      emphasis:   rgba(BRAND.burgundy, OPACITY_WHITE.borderEmphasis),
      default:    rgba(BRAND.burgundy, OPACITY_WHITE.borderDefault),
      subtle:     rgba(BRAND.burgundy, OPACITY_WHITE.borderSubtle),
    },
  }
}

// Resolved themes. Frozen so components can't accidentally mutate.
export const THEMES = Object.freeze({
  red:   buildRedTheme(),
  white: buildWhiteTheme(),
})

// Default theme name used when a user has no themeMode set on their
// profile and during SSR / pre-fetch render.
export const DEFAULT_THEME = 'red'

// Resolve an arbitrary themeMode string to a guaranteed-valid theme.
// Anything we don't recognize falls back to the default rather than
// returning undefined and crashing components.
export function resolveTheme(themeMode) {
  return THEMES[themeMode] || THEMES[DEFAULT_THEME]
}

// Re-export the brand palette for the rare case a component genuinely
// needs a brand color regardless of theme (e.g. the Stripe checkout
// modal which renders inside an iframe with its own dashboard config).
export { BRAND }
